import React, { useMemo, useCallback } from 'react';
import { Form } from 'react-final-form';
import arrayMutators from 'final-form-arrays';
import css from './SectionOfferingsForm.module.css';
import { Button, PrimaryButton, SecondaryButton } from '../../../components';
import { FormattedMessage } from 'react-intl';

import AutographFormFields from './AutographFormFields/AutographFormFields';
import ShoutoutOrGreetingsFormFields from './ShoutoutOrGreetingsFormFields/ShoutoutOrGreetingsFormFields';
import SocialMediaFormFields from './SocialMediaFormFields/SocialMediaFormFields';
import CreativeMediaFormFields from './CreativeMediaFormFields/CreativeMediaFormFields';
import AppearancesFormFields from './AppearancesFormFields/AppearancesFormFields';
import ExclusiveTrainingFormFields from './ExclusiveTrainingFormFields/ExclusiveTrainingFormFields';
import MentorshipFormFields from './MentorshipFormFields/MentorshipFormFields';
import BusinessDevelopmentFormFields from './BusinessDevelopmentFormFields/BusinessDevelopmentFormFields';
import { EnhancedServiceTypes, SignatureServiceTypes } from '../../../util/enums';

const SectionOfferingsForm = ({
  initialValues = {},
  onSubmit,
  photoOptions,
  type,
  platform,
  listing,
}) => {
  const formId = 'ServiceOfferingsForm';

  // Memoize form field renderer to prevent unnecessary re-renders
  const FormFieldsComponent = useMemo(() => {
    const componentMap = {
      [SignatureServiceTypes.AUTOGRAPHS]: AutographFormFields,
      [SignatureServiceTypes.SHOUTOUTS_OR_GREETINGS]: ShoutoutOrGreetingsFormFields,
      [SignatureServiceTypes.SOCIAL_MEDIA]: SocialMediaFormFields,
      [SignatureServiceTypes.CREATIVE_MEDIA]: CreativeMediaFormFields,
      [SignatureServiceTypes.APPEARANCES]: AppearancesFormFields,
      [EnhancedServiceTypes.EXCLUSIVE_TRAINING]: ExclusiveTrainingFormFields,
      [EnhancedServiceTypes.MENTORSHIP]: MentorshipFormFields,
      [EnhancedServiceTypes.BUSINESS_DEVELOPMENT]: BusinessDevelopmentFormFields,
    };

    return componentMap[type] || null;
  }, [type]);

  // Memoize props for form fields
  const formFieldProps = useMemo(() => {
    const baseProps = { formId, listing };

    // Add type-specific props
    if (type === SignatureServiceTypes.AUTOGRAPHS) {
      return {
        ...baseProps,
        photoOptions,
      };
    }

    // For other types that need platform
    if (
      [
        SignatureServiceTypes.SOCIAL_MEDIA,
        SignatureServiceTypes.CREATIVE_MEDIA,
        SignatureServiceTypes.APPEARANCES,
        EnhancedServiceTypes.EXCLUSIVE_TRAINING,
        EnhancedServiceTypes.MENTORSHIP,
        EnhancedServiceTypes.BUSINESS_DEVELOPMENT,
      ].includes(type)
    ) {
      return { ...baseProps, platform };
    }

    return baseProps;
  }, [type, formId, photoOptions, platform]);

  // Memoize submit handler
  const handleSubmit = useCallback(
    values => {
      if (process.env.NODE_ENV === 'development') {
        // console.log('Form submitted:', values);
      }
      onSubmit?.(values);
    },
    [onSubmit]
  );

  // Memoize reset handler
  const handleReset = useCallback(
    form => () => {
      form.reset();
    },
    []
  );

  // Early return if no form component
  if (!FormFieldsComponent) {
    return null;
  }
  return (
    <Form
      keepDirtyOnReinitialize
      onSubmit={handleSubmit}
      initialValues={initialValues}
      mutators={{ ...arrayMutators }}
      render={({ handleSubmit, form, submitting, pristine, values }) => {
        const isDirty = !pristine;
        return (
          <form onSubmit={handleSubmit} className={css.form} noValidate>
            {/* Render form fields with memoized props */}
            <FormFieldsComponent {...formFieldProps} values={values} />

            {/* Action buttons */}
            <div className={css.buttonGroup}>
              <SecondaryButton
                type="button"
                onClick={handleReset(form)}
                disabled={submitting || !isDirty}
                className={css.resetButton}
                aria-label="Cancel and reset form"
              >
                <FormattedMessage id="SectionOfferingsForm.cancel" />
              </SecondaryButton>

              <PrimaryButton
                type="submit"
                disabled={submitting || !isDirty}
                inProgress={submitting}
                className={css.submitButton}
                aria-label={submitting ? 'Saving changes' : 'Submit form'}
              >
                {submitting ? (
                  <FormattedMessage id="SectionOfferingsForm.saving" />
                ) : (
                  <FormattedMessage id="SectionOfferingsForm.buyNow" />
                )}
              </PrimaryButton>
            </div>
          </form>
        );
      }}
    />
  );
};

export default React.memo(SectionOfferingsForm);
